// JSON-LD builders. Every node is @id-addressable so pages can reference the
// same Organization / WebSite entity instead of redeclaring it — that's what
// lets Google resolve "Sigma Enterprises" into a single knowledge-graph entity
// rather than a dozen unrelated mentions.
//
// Deliberately NOT emitted anywhere: Review / AggregateRating markup for the
// business itself. Google's structured-data policy makes self-serving reviews
// collected on your own site ineligible for rich results, and marking them up
// risks a manual action. The testimonials stay as visible on-page content.

import { SITE_URL, BUSINESS, SAME_AS, absUrl } from "./site";

const ORG_ID = `${SITE_URL}/#organization`;
const SITE_ID = `${SITE_URL}/#website`;
const PLACE_ID = `${SITE_URL}/#place`;

const postalAddress = {
  "@type": "PostalAddress",
  streetAddress: BUSINESS.address.street,
  addressLocality: BUSINESS.address.locality,
  addressRegion: BUSINESS.address.region,
  postalCode: BUSINESS.address.postalCode,
  addressCountry: BUSINESS.address.country,
};

const openingHoursSpecification = BUSINESS.openingHours.map((block) => ({
  "@type": "OpeningHoursSpecification",
  dayOfWeek: block.days,
  opens: block.opens,
  closes: block.closes,
}));

/**
 * The core business entity. Typed as both Organization and LocalBusiness so it
 * satisfies the knowledge-panel path and the local-pack path from one node.
 *
 * @param {Array} categories Catalogue categories from src/sigma/data.js — used
 *   to build the offer catalogue so Google can see what is actually stocked.
 */
export function organizationSchema(categories = []) {
  const node = {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness", "Wholesaler"],
    "@id": ORG_ID,
    name: BUSINESS.name,
    alternateName: BUSINESS.alternateName,
    legalName: BUSINESS.legalName,
    description: BUSINESS.description,
    url: `${SITE_URL}/`,
    foundingDate: BUSINESS.foundingYear,
    telephone: BUSINESS.phoneE164,
    address: postalAddress,
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS.geo.lat,
      longitude: BUSINESS.geo.lng,
    },
    hasMap: BUSINESS.mapLink,
    openingHoursSpecification,
    priceRange: BUSINESS.priceRange,
    currenciesAccepted: BUSINESS.currenciesAccepted,
    // Delivery areas and export markets both belong in areaServed — the
    // property describes where the business will serve a customer, which is
    // exactly what an export market is. Countries are emitted as Country so
    // the distinction from a domestic delivery area stays machine-readable.
    areaServed: [
      ...BUSINESS.areasServed.map((a) => ({
        "@type": "AdministrativeArea",
        name: a,
      })),
      ...BUSINESS.exportMarkets.map((c) => ({
        "@type": c === "Europe" ? "AdministrativeArea" : "Country",
        name: c,
      })),
    ],
    knowsLanguage: ["en", "hi"],
    logo: {
      "@type": "ImageObject",
      "@id": `${SITE_URL}/#logo`,
      url: absUrl("/logo512.png"),
      width: 512,
      height: 512,
      caption: BUSINESS.name,
    },
    image: { "@id": `${SITE_URL}/#logo` },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: BUSINESS.phoneE164,
        contactType: "sales",
        areaServed: "IN",
        availableLanguage: ["en", "hi"],
      },
    ],
  };

  if (SAME_AS.length) node.sameAs = SAME_AS;

  // What the business deals in, as subject matter rather than as a catalogue
  // of Offers.
  //
  // This was a hasOfferCatalog of Offer -> Product nodes. It read well, but
  // every one of those Products lacked a price, because trade pricing here is
  // quoted per enquiry. Google validates any Product node it finds and rejects
  // one with no offers, review or aggregateRating, so the markup earned a
  // permanent "Product snippets" error in Search Console and no rich result in
  // return. knowsAbout says the same thing about what is stocked without
  // making a commercial claim Google will measure and fail.
  if (categories.length) {
    node.knowsAbout = [
      ...categories.map((cat) => cat.name),
      ...categories.flatMap((cat) => (cat.items || []).map((item) => item.name)),
    ];
  }

  return node;
}

/** The Place node — reinforces the physical location for local search. */
export function placeSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Place",
    "@id": PLACE_ID,
    name: BUSINESS.name,
    address: postalAddress,
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS.geo.lat,
      longitude: BUSINESS.geo.lng,
    },
    hasMap: BUSINESS.mapLink,
  };
}

/** Site-level node. No SearchAction — the site has no internal search. */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": SITE_ID,
    url: `${SITE_URL}/`,
    name: BUSINESS.name,
    alternateName: BUSINESS.alternateName,
    description: BUSINESS.description,
    publisher: { "@id": ORG_ID },
    inLanguage: "en-IN",
  };
}

/**
 * Page node. `type` accepts any WebPage subtype (CollectionPage, ContactPage,
 * AboutPage, FAQPage…).
 */
export function webPageSchema({
  path,
  name,
  description,
  type = "WebPage",
  primaryImage,
  breadcrumbId,
}) {
  const url = absUrl(path);
  const node = {
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    isPartOf: { "@id": SITE_ID },
    about: { "@id": ORG_ID },
    inLanguage: "en-IN",
  };
  if (primaryImage) {
    node.primaryImageOfPage = {
      "@type": "ImageObject",
      url: absUrl(primaryImage),
    };
  }
  if (breadcrumbId) node.breadcrumb = { "@id": breadcrumbId };
  return node;
}

/**
 * @param {Array<{name:string,path:string}>} crumbs Ordered, including Home.
 */
export function breadcrumbSchema(crumbs, pagePath) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${absUrl(pagePath)}#breadcrumb`,
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absUrl(c.path),
    })),
  };
}

/**
 * ItemList for a category or catalogue listing.
 *
 * `anchor` distinguishes two lists on the same page. /catalogue carries one
 * list of categories and one of the individual grades; without it both would
 * claim the same @id and the second would silently overwrite the first in the
 * graph Google builds.
 */
export function itemListSchema({ path, name, items, anchor = "itemlist" }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${absUrl(path)}#${anchor}`,
    name,
    numberOfItems: items.length,
    itemListOrder: "https://schema.org/ItemListUnordered",
    // ListItem carries name and url only.
    //
    // Each entry used to nest a Product node. That was wrong twice over: it
    // produced a priceless Product for Google to reject on every listing page,
    // and on /brands it typed twelve paper mills as Products, which they are
    // not — they are manufacturers whose goods are stocked, not goods on sale.
    // A plain ItemList communicates the ordering and the destinations, which is
    // all this markup was ever read for.
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      ...(item.path ? { url: absUrl(item.path) } : {}),
    })),
  };
}

/**
 * Grade specifications, as a WebPage-safe property list.
 *
 * This replaces a Product node, and the reason is worth keeping. A Product in
 * Google's eyes is something with a price, a review or a rating; it validates
 * every Product it finds against that and rejects the rest. These pages have
 * none of the three and cannot honestly acquire them — trade pricing is quoted
 * per enquiry, and reviews collected on your own site about your own business
 * are ineligible by policy. So the Product node could never produce a rich
 * result, only a recurring "Product snippets" error in Search Console. Google
 * confirmed exactly that on 5 Aug 2026.
 *
 * The grammage and finish still reach machines: they are rendered as a visible
 * <dl> on the page, which is what a crawler or an answer engine reads anyway.
 * What is dropped is a claim we were never able to substantiate, not data.
 */
export function specListSchema({ path, name, specs = [] }) {
  const props = (specs || []).filter((s) => s && s.label && s.value);
  if (!props.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${absUrl(path)}#specifications`,
    name: `${name} — specifications`,
    itemListElement: props.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: s.label,
      description: s.value,
    })),
  };
}

/**
 * @param {Array<{q:string,a:string}>} faqs Answers must be plain text that also
 *   appears visibly on the page — invisible FAQ markup is a policy violation.
 */
export function faqSchema(faqs, path) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${absUrl(path)}#faq`,
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export { ORG_ID, SITE_ID, PLACE_ID };
